import * as React from "react";
import { GraphCommand } from "./interfaces/config";
import Legend from "./Legend";
import TooltipHandler from "./TooltipHandler";
import Brush from "./Brush";
import "./Plot.scss";
import { GraphAxis } from "./GraphAxis";
import { AxisType, AxisMode } from "./interfaces/axis";
import GraphGL from "./GraphGL";
import memoize from "memoize-one";
import { ObjectType } from "./interfaces/object";
import { GraphTextObject } from "./GraphTextObject";
import PanZoom from "./PanZoom";
import HistogramBars from "./HistogramBars";
import { GraphGrid } from "./GraphGrid";
import { PolarScaleHandler } from "./PolarScaleHandler";
import { getElementYId, getIsAnyY2Visible, filterVisibleData, buildLegendProperties, buildObjectProperties, getScaledRadius, } from "./utils";
import { MarginContext, CLOSED_RIGHT_MARGIN, OPEN_RIGHT_MARGIN, } from "./Containers/MarginContextContainer";
import { GraphSettingsContainerWrapper, ScaleContext, } from "./Containers/GraphSettingsContainer";
import { MousePositionHandler } from "./MousePositionHandler";
import { PointFinderContainer } from "./SelectableComponents/PointFinderContainer";
import { PointPopupWrapper } from "./SelectableComponents/PointPopupWrapper";
import { VisibleValuesContext } from "./Containers/VisibleValuesContext";
import { PrintableLegend } from "./Legend/PrintableLegend";
export class GraphBase extends React.PureComponent {
    constructor(props) {
        super(props);
        this.getGraphObject = memoize((data, graph) => {
            var _a;
            return ((_a = data === null || data === void 0 ? void 0 : data.objects) === null || _a === void 0 ? void 0 : _a.length) > 0
                ? data.objects.filter((obj) => obj.graphId === graph.id)
                : [];
        });
        this.filterVisibleData = memoize(filterVisibleData);
        this.buildLegendProperties = memoize(buildLegendProperties);
        this.buildObjectProperties = memoize(buildObjectProperties);
        this.handleLegendClick = () => {
            const { setIsLegendOpen, setRightMargin } = this
                .context;
            setIsLegendOpen((prevLegendState) => {
                const newLegendState = !prevLegendState;
                if (newLegendState) {
                    setRightMargin(OPEN_RIGHT_MARGIN);
                }
                else {
                    setRightMargin(CLOSED_RIGHT_MARGIN);
                }
                setIsLegendOpen(newLegendState);
                this.props.toggleLegend();
                return newLegendState;
            });
        };
        const { graph, ignoreFileName } = props;
        const values = graph.elements.map((_, i) => getElementYId(graph.elements[i], ignoreFileName));
        this.state = {
            values,
            searchRadius: 30,
        };
    }
    componentDidMount() {
        const { isLegendOpen } = this.props;
        const { setRightMargin } = this.context;
        if (!isLegendOpen) {
            setRightMargin(CLOSED_RIGHT_MARGIN);
        }
        else {
            setRightMargin(OPEN_RIGHT_MARGIN);
        }
    }
    render() {
        const { graph, data, ignoreFileName, xValues, yValues, brush, showPopup } = this.props;
        return (React.createElement(VisibleValuesContext.Consumer, null, ({ visibleValues }) => {
            if (!visibleValues) {
                return null;
            }
            const dataset = data.data;
            for (let value of yValues) {
                if (!dataset.hasOwnProperty(value)) {
                    dataset[value] = [];
                }
            }
            const { filteredDataset, filteredYValues, filteredY2Values, filteredXValues, filteredElements, quadtreeMatrix, } = this.filterVisibleData(dataset, xValues, graph.elements, visibleValues, ignoreFileName);
            const { width, height } = this.props.svgDimensions;
            const clipId = `clip_${graph.id}`;
            const xAxisConfig = this.props.graph.axes.filter((a) => a.type === AxisType.x)[0];
            const yAxisConfig = this.props.graph.axes.filter((a) => a.type === AxisType.y)[0];
            const y2AxisConfig = this.props.graph.axes.filter((a) => a.type === AxisType.y2)[0];
            const objects = this.getGraphObject(data, graph);
            const isHistogram = graph.command === GraphCommand.bar;
            const labelWidth = 140;
            const labelHeight = 11;
            const isAnyY2Visible = getIsAnyY2Visible(visibleValues, graph.elements);
            const { searchRadius } = this.state;
            return (React.createElement(MarginContext.Consumer, null, ({ margins }) => {
                const allAxisConfig = {
                    x: xAxisConfig,
                    y: yAxisConfig,
                    y2: y2AxisConfig,
                };
                const allValues = {
                    x: filteredXValues,
                    y: filteredYValues,
                    y2: filteredY2Values,
                };
                const allApplyPadding = {
                    x: true,
                    y: graph.command !== GraphCommand.bar,
                    y2: true,
                };
                const plotWidth = width - margins.left - margins.right > 0
                    ? width - margins.left - margins.right
                    : 0;
                const plotHeight = height - margins.top - margins.bottom > 0
                    ? height - margins.top - margins.bottom
                    : 0;
                const plotWrapperWidth = width -
                    (this.props.isLegendOpen
                        ? OPEN_RIGHT_MARGIN
                        : CLOSED_RIGHT_MARGIN);
                return (React.createElement(GraphSettingsContainerWrapper, { data: filteredDataset, allAxisConfig: allAxisConfig, objects: objects, ignoreFileName: ignoreFileName, svgDimensions: this.props.svgDimensions, allValues: allValues, isHistogram: isHistogram, elements: filteredElements, margins: margins, allApplyPadding: allApplyPadding },
                    React.createElement(ScaleContext.Consumer, null, ({ yScale, xScale, y2Scale, yExtend, xExtend, y2Extend, }) => {
                        const scaledRadius = getScaledRadius(xScale, yScale, y2Scale, searchRadius);
                        return (React.createElement(React.Fragment, null,
                            React.createElement(PolarScaleHandler, { xScale: xScale, yScale: yScale, showAsPolar: this.props.showAsPolar, render: (yScale, xScale) => (React.createElement(MousePositionHandler, { graph: graph, xScale: xScale, yScale: yScale, yScale2: y2Scale, render: (mousePos, getArc, onMouseMove, resetPosition, mouseScaled) => (React.createElement("div", { className: "general-plot" },
                                        React.createElement("div", { "data-testid": "plot-base", className: "plot-base", ref: this.props.isImage2Download
                                                ? this.props.plotContainerRef
                                                : undefined, style: {
                                                height: height + "px",
                                                width: plotWrapperWidth + "px",
                                            }, onMouseLeave: resetPosition },
                                            React.createElement("svg", { width: plotWrapperWidth, height: height, ref: getArc, xmlns: "http://www.w3.org/2000/svg", className: "mainSvg" },
                                                React.createElement("g", { transform: `translate(0, 0)` },
                                                    React.createElement(GraphGrid, { graph: graph, yScale: yScale, xScale: xScale, clipId: clipId, margins: margins, height: height, width: width, labelWidth: labelWidth, labelHeight: labelHeight, yId: "y1" }),
                                                    isAnyY2Visible && (React.createElement(GraphGrid, { graph: graph, yScale: y2Scale, xScale: xScale, clipId: clipId, margins: margins, height: height, width: width, labelWidth: labelWidth, labelHeight: labelHeight, yId: "y2" })),
                                                    React.createElement(GraphAxis, { axis: xAxisConfig, dimensions: this.props.svgDimensions, scale: xScale, margins: margins, labelWidth: labelWidth }),
                                                    React.createElement(GraphAxis, { axis: yAxisConfig, dimensions: this.props.svgDimensions, scale: yScale, margins: margins, labelWidth: labelWidth }),
                                                    isAnyY2Visible && (React.createElement(GraphAxis, { axis: y2AxisConfig, dimensions: this.props.svgDimensions, scale: y2Scale, margins: margins, labelWidth: labelWidth }))),
                                                React.createElement("clipPath", { id: clipId },
                                                    React.createElement("rect", { x: margins.left, y: margins.top, width: plotWidth, height: plotHeight })),
                                                objects.map((object, index) => object.type ===
                                                    ObjectType.text && (React.createElement(GraphTextObject, { key: `${object === null || object === void 0 ? void 0 : object.text}_${index}`, clipId: clipId, object: object, xScale: xScale, yScale: yScale, margins: margins }))),
                                                isHistogram ? (React.createElement(HistogramBars, { elements: graph.elements, data: data, ignoreFileName: ignoreFileName, objects: objects, xScale: xScale, yScale: yScale, clipId: clipId })) : null),
                                            React.createElement(GraphGL, { width: width, height: height, margins: margins, elements: graph.elements, data: data, ignoreFileName: ignoreFileName, xValues: xValues, isXLog: xAxisConfig.mode ===
                                                    AxisMode.Logarithmic, isYLog: yAxisConfig.mode ===
                                                    AxisMode.Logarithmic, glCanvasRef: this.props.glCanvasRef, objects: objects, xScale: xScale, yScale: yScale, y2Scale: y2Scale, visibleValues: visibleValues, isHistogram: isHistogram, allValuesPositive: yExtend[0] > 0 }),
                                            showPopup ? (React.createElement(PointFinderContainer, { datasetMatrix: quadtreeMatrix, scaledRadius: scaledRadius, mousePositionScaled: mouseScaled, visibleValues: visibleValues, y2Values: filteredY2Values, y2Scale: y2Scale, render: (point) => (React.createElement(PointPopupWrapper, { scales: {
                                                        xScale,
                                                        yScale,
                                                        y2Scale,
                                                    }, point2show: point, svgDimensions: {
                                                        height: plotHeight,
                                                        width: plotWidth,
                                                    }, margins: margins })) })) : null,
                                            brush ? (React.createElement(Brush, { width: width, height: height, xScale: xScale, yScale: yScale, y2Scale: y2Scale, margins: margins, onMousemove: onMouseMove, onScaleChange: (xScale, yScale, y2Scale) => {
                                                    xExtend.updateExtend(xScale);
                                                    yExtend.updateExtend(yScale);
                                                    if (y2Scale) {
                                                        y2Extend.updateExtend(y2Scale);
                                                    }
                                                } })) : (React.createElement(PanZoom, { width: width, height: height, xScale: xScale, yScale: yScale, y2Scale: y2Scale, yExtend: yExtend.extend, xExtend: xExtend.extend, y2Extend: y2Extend.extend, margins: margins, onMousemove: onMouseMove, onScaleChange: (xScale, yScale, y2Scale) => {
                                                    xExtend.updateExtend(xScale);
                                                    yExtend.updateExtend(yScale);
                                                    if (y2Scale) {
                                                        y2Extend.updateExtend(y2Scale);
                                                    }
                                                } }))),
                                        React.createElement("div", { className: "graph-right-side", style: {
                                                top: margins.top,
                                                height: plotHeight,
                                            } },
                                            this.props.hideTitle ||
                                                this.props.isImage2Download ? null : (React.createElement(TooltipHandler, { isNumber: xAxisConfig.mode !== AxisMode.Date, xScale: xScale, yScale: yScale, y2Scale: y2Scale, mouse: mousePos, isAnyY2Visible: isAnyY2Visible, marginRight: margins.right })),
                                            !this.props.hideTitle &&
                                                (this.props.isImage2Download ? (React.createElement(PrintableLegend, { hidden: !this.props.isLegendOpen, width: margins.right, height: plotHeight, legendContainerRef: this.props.legendContainerRef, legendProperties: this.buildLegendProperties(graph, ignoreFileName), objectProperties: this.buildObjectProperties(this.props.graph.objects), showLabel: isAnyY2Visible })) : (React.createElement(Legend, { hidden: !this.props.isLegendOpen, height: this.props.legendHeight, margins: margins, legendProperties: this.buildLegendProperties(graph, ignoreFileName), objectProperties: this.buildObjectProperties(this.props.graph.objects), toggleHidden: this.handleLegendClick, showLabel: isAnyY2Visible })))))) })) })));
                    })));
            }));
        }));
    }
}
GraphBase.contextType = MarginContext;
GraphBase.contextType = MarginContext;
//# sourceMappingURL=GraphBase.js.map